/**
 * SoleadLab 2.0 - Indication Preview
 *
 * Compact floating info card that appears near a selected indication on the map.
 * Shows photo thumbnail + inline-editable type, fields, and notes.
 * No backdrop — map stays fully interactive (pan, zoom, drag features).
 *
 * Auto-flips below the marker when there isn't enough room above.
 *
 * REFERENCE: editor (23).html - openInfoPopup / buildInfoHtml
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useProjectStore, useMapStore, INDICATION_TYPES } from '../../stores';
import { useTaskStore } from '../../stores/taskStore';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { resolveImageSrc, centroidOfPolygon, retypedStringOcGeometry, indCenter } from '../../utils';
import { deriveObservations } from '../../utils/observations';
import { captureDay } from '../../utils/captureDate';
import { useDerivedTopologyPlan } from '../../domain/topology/useDerivedTopology';
import { humanEditProvenance } from '../../domain/topology/applyDerivedTopology';
import ThermalAnnotator from './thermal/ThermalAnnotator';
import ZoomableImage from './ZoomableImage';
import FieldPhotosStrip, { isRenderableFieldPhoto } from './FieldPhotosStrip';
import { driveThumbnailUrl, parseDriveIdFromUrl } from '../../utils/driveImport';

/** Resolve a photo path/Drive-ID pair to the FAST sized variant (Drive photos)
 *  or the plain resolved path (R2 assets, local blobs — already sized sanely).
 *  Legacy markers baked width-less Drive URLs, so the ID is re-parsed from the
 *  path when no stamped ID exists. 800px is pixel-perfect for the ~350px popup
 *  pane (~250KB vs ~900KB at 2000px); zooming fetches the full original. */
function sizedPhotoSrc(path?: string, driveId?: string): string | undefined {
  const id = driveId ?? (path ? parseDriveIdFromUrl(path) : null);
  if (id) return driveThumbnailUrl(id, 800);
  return path ? resolveImageSrc(path) : undefined;
}

/** Full-resolution companion for `sizedPhotoSrc` (w=0 → proxy serves the
 *  original). Undefined for non-Drive paths — those are already full-res. */
function fullPhotoSrc(path?: string, driveId?: string): string | undefined {
  const id = driveId ?? (path ? parseDriveIdFromUrl(path) : null);
  return id ? driveThumbnailUrl(id, 0) : undefined;
}
import './IndicationPreview.css';

interface IndicationPreviewProps {
  indicationId: string;
  map: MapboxMap;
  onEditFields: (id: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

/** Pixel gap between arrow tip and marker edge */
const ARROW_GAP = 14;

/** Field definitions for compact inline editing */
const INLINE_FIELDS = [
  { key: 'station', label: 'Sta' },
  { key: 'inverter', label: 'Inv' },
  { key: 'mppt', label: 'MPPT' },
  { key: 'dccb', label: 'DCCB' },
  { key: 'string', label: 'Str' },
  { key: 'module', label: 'Mod' },
] as const;

/** Topology level colors — matches the label popup / layers legend
 *  (string uses the darker green: these inputs sit on a light card). */
const FIELD_VALUE_COLORS: Record<string, string> = {
  station: '#ef4444',
  inverter: '#3b82f6',
  mppt: '#0891b2',
  dccb: '#ea580c',
  string: '#16a34a',
  module: '#9333ea',
};

/** Content-fitted input width: values like "DC01" or "E24" were clipped by
 *  the old fixed 28px box. */
function fieldWidth(value: string): React.CSSProperties {
  return { width: `${Math.max(2, value.length) + 1.5}ch` };
}

export default function IndicationPreview({
  indicationId,
  map,
  onEditFields,
  onDelete,
  onClose,
}: IndicationPreviewProps) {
  const indication = useProjectStore((s) =>
    s.data.indications.find((i) => i.id === indicationId)
  );
  const updateIndication = useProjectStore((s) => s.updateIndication);
  const isReadOnly = useIsReadOnlyViewer();
  const indicationViewMode = useMapStore((s) => s.indicationViewMode);
  const visibleDates = useProjectStore((s) => s.visibleDates);

  const cardRef = useRef<HTMLDivElement>(null);
  const thermalImgRef = useRef<HTMLImageElement>(null);
  const noop = useCallback(() => {}, []);

  // Position + flip state
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [flipped, setFlipped] = useState(false);

  const updatePosition = useCallback(() => {
    if (!indication) return;

    const cardHeight = cardRef.current?.offsetHeight ?? 300;

    let minY = Infinity;
    let maxY = -Infinity;
    let centerX = 0;

    if (indication.geometry.type === 'Polygon') {
      const ring = indication.geometry.coordinates[0] as [number, number][];
      let sumX = 0;
      ring.forEach((coord) => {
        const pt = map.project(coord);
        if (pt.y < minY) minY = pt.y;
        if (pt.y > maxY) maxY = pt.y;
        sumX += pt.x;
      });
      centerX = sumX / ring.length;
    } else if (indication.geometry.type === 'Point') {
      const pt = map.project(indication.geometry.coordinates as [number, number]);
      minY = pt.y;
      maxY = pt.y;
      centerX = pt.x;
    } else {
      return;
    }

    const spaceAbove = minY - ARROW_GAP;
    const needsFlip = spaceAbove < cardHeight + 10;

    setFlipped(needsFlip);

    if (needsFlip) {
      setPos({ x: centerX, y: maxY + ARROW_GAP });
    } else {
      setPos({ x: centerX, y: minY - ARROW_GAP });
    }
  }, [indication, map]);

  // Position on mount + reposition on map move/zoom
  useEffect(() => {
    updatePosition();
    map.on('move', updatePosition);
    return () => { map.off('move', updatePosition); };
  }, [map, updatePosition]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // If indication was deleted externally, close
  useEffect(() => {
    if (!indication) onClose();
  }, [indication, onClose]);

  // ── Grid plan for the chip (2026-09-02 rewrite) ──
  // The old effect here WROTE topology to the server merely because the
  // card was opened (write-on-view, not isReadOnly-gated). Killed: fills
  // happen at creation and on explicit actions only. The chip below shows
  // where the grid disagrees (or could fill a blank) and writes ONLY on an
  // explicit Accept — and never renders for share-link viewers (owner:
  // client-facing views stay clean).
  const plan = useDerivedTopologyPlan(isReadOnly ? null : indication);
  const chipEntries = useMemo(() => {
    if (!plan) return [];
    const entries: Array<{ key: string; value: string; conflict?: string }> = [];
    for (const c of plan.conflicts) entries.push({ key: c.field, value: c.derived, conflict: c.stored });
    if (plan.updates) {
      for (const [k, v] of Object.entries(plan.updates)) {
        if (k === 'topologyProvenance' || k === 'customFields' || typeof v !== 'string') continue;
        entries.push({ key: k, value: v });
      }
    }
    return entries;
  }, [plan]);

  if (!indication || !pos) return null;

  // ── Helpers to update store directly ──
  const handleTypeChange = (newType: string) => {
    // String OC: rebuild the rect around the marker's centre as the FULL
    // string under it (grid count, rows, bearing) — the centre stays put;
    // off-grid the default rect at the current rotation, as before.
    if (newType === 'String OC') {
      let center: [number, number] = [0, 0];
      if (indication.geometry.type === 'Polygon') {
        center = centroidOfPolygon(indication.geometry.coordinates[0] as [number, number][]) || [0, 0];
      } else if (indication.geometry.type === 'Point') {
        center = indication.geometry.coordinates as [number, number];
      }

      const { geometry: finalGeom, rotationDeg } = retypedStringOcGeometry(
        center,
        indication.properties.rotationDeg || 0,
      );

      updateIndication(indicationId, {
        type: newType as any,
        geometry: finalGeom,
        properties: { title: newType, rotationDeg },
      });
      return;
    }

    updateIndication(indicationId, {
      type: newType as any,
      properties: { title: newType },
    });
  };

  const handleFieldChange = (field: string, value: string) => {
    // A typed value is a HUMAN value — stamp it so re-derives and the
    // per-site bulk-accept never silently displace it.
    const isTopoField = ['station', 'inverter', 'mppt', 'dccb', 'string', 'module'].includes(field);
    updateIndication(indicationId, {
      properties: {
        [field]: value,
        ...(isTopoField && indication
          ? humanEditProvenance(
              indication.properties as unknown as Record<string, unknown>,
              [field],
            )
          : {}),
      },
    });
  };

  const handleNotesChange = (value: string) => {
    updateIndication(indicationId, {
      properties: { description: value },
    });
  };

  const typeConfig = INDICATION_TYPES[indication.type as keyof typeof INDICATION_TYPES];
  const typeColor = typeConfig?.color || '#888';

  // Photo + thermal overlay — a child fault (PFC-4B Model B) has no imagery of
  // its own; resolve its SOURCE photo (thermal image AND the visual/zoom pair)
  // via sourcePhotoId, for display only (read-only; never written to the child).
  // The child's OWN ROI (thermalData) overlays on top.
  // #5 cross-inspection: resolve imagery from the observation being VIEWED —
  // Current → latest; As-found → the observation for the visible inspection day —
  // so filtering to an earlier year shows THAT year's photo, not the newest.
  // (Plain const, not a hook — this component early-returns above.)
  const obsList = deriveObservations(indication);
  const activeObs = indicationViewMode === 'current'
    ? obsList[obsList.length - 1]
    : (() => {
        const visible = new Set(visibleDates);
        const inView = obsList.filter((o) => o.date && visible.has(captureDay(o.date)));
        return inView.length ? inView[inView.length - 1] : obsList[obsList.length - 1];
      })();
  const ownImagePath = indication.properties.image || '';
  const sourcePhoto =
    !ownImagePath && activeObs?.sourcePhotoId
      ? useProjectStore.getState().data.indications.find((i) => i.id === activeObs.sourcePhotoId)
      : undefined;
  const imageryProps = sourcePhoto?.properties ?? indication.properties;
  const imagePath = imageryProps.image || '';
  // Sized-first + progressive full-res: Drive-hosted primaries (visual-only
  // imports) open on the ~10x smaller thumbnail; ZoomableImage swaps in the
  // original after a short dwell. R2/local paths resolve unchanged.
  const imageSrc = (imagePath ? sizedPhotoSrc(imagePath) : '') || '';
  const imageFullSrc = imagePath ? fullPhotoSrc(imagePath) : undefined;
  // STRICTLY the active observation's thermal — no top-level fallback, so a year
  // with no analysis shows no ROI instead of carrying over the other year's.
  const thermalData = activeObs?.thermalData;
  const hasThermalOverlay = thermalData &&
    ((thermalData.rois?.length ?? 0) > 0 || (thermalData.points?.length ?? 0) > 0);

  // User-entered notes
  const userNotes = indication.properties.description || '';

  // Compute centroid for coordinate display
  let coordStr = '';
  if (indication.geometry.type === 'Polygon') {
    const c = centroidOfPolygon(indication.geometry.coordinates[0] as [number, number][]);
    if (c) coordStr = `${c[0].toFixed(5)}, ${c[1].toFixed(5)}`;
  }

  return (
    <div
      ref={cardRef}
      className={`indication-preview${flipped ? ' indication-preview--flipped' : ''}`}
      style={{ left: pos.x, top: pos.y }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="indication-preview__inner">
        {/* Header — type dropdown + coords */}
        <div className="indication-preview__header">
          <span className="indication-preview__type-dot" style={{ background: typeColor }} />
          <select
            className="indication-preview__type-select"
            value={indication.type}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            {Object.entries(INDICATION_TYPES).map(([type, config]) => (
              <option key={type} value={type}>{config.label}</option>
            ))}
          </select>
          {coordStr && <span className="indication-preview__coords">{coordStr}</span>}
        </div>

        {/* Custom type name — editable inline when type is 'Custom' */}
        {indication.type === 'Custom' && (
          <input
            className="indication-preview__custom-name"
            type="text"
            placeholder="Custom type name (e.g. Fence damage)"
            defaultValue={indication.properties.customTypeName || ''}
            onBlur={(e) => handleFieldChange('customTypeName', e.target.value.trim())}
          />
        )}

        {/* Video player — for Video type indications */}
        {imageSrc && indication.type === 'Video' && (
          <video
            className="indication-preview__video"
            src={imageSrc}
            controls
            preload="metadata"
          />
        )}

        {/* Which inspection's photo is shown (#5) — unambiguous under As-found + date filter. */}
        {imageSrc && activeObs && (
          <div className="indication-preview__obs-badge" data-testid="preview-obs-badge">
            {indicationViewMode === 'asFound' ? 'As found' : 'Current'}
            {activeObs.date ? ` · ${captureDay(activeObs.date)}` : ''}
          </div>
        )}

        {/* Photo — Thermal/Visual/Zoom toggle + image */}
        {imageSrc && indication.type !== 'Video' && <PhotoSection
          imageSrc={imageSrc}
          imageFullSrc={imageFullSrc}
          thermalData={thermalData}
          hasThermalOverlay={hasThermalOverlay}
          thermalImgRef={thermalImgRef}
          noop={noop}
          visualImage={sizedPhotoSrc(imageryProps.visualImage, imageryProps.visualDriveId)}
          visualFullSrc={fullPhotoSrc(imageryProps.visualImage, imageryProps.visualDriveId)}
          zoomImage={sizedPhotoSrc(imageryProps.zoomImage, imageryProps.zoomDriveId)}
          zoomFullSrc={fullPhotoSrc(imageryProps.zoomImage, imageryProps.zoomDriveId)}
        />}

        {/* FL-3a: field photos — read-only strip, separate from the source
            photo above (field photos never replace the display image). The
            <img> auth rides sl_refresh (owner) or sl_share (visitors). */}
        {(() => {
          // Gate on what the strip will actually RENDER, not on the raw array:
          // the strip drops entries whose key is not a well-formed field-photo
          // key, and gating on the raw length would leave a "Field photos"
          // heading over an empty strip.
          const shown = (indication.properties.fieldPhotos ?? []).filter(isRenderableFieldPhoto);
          if (shown.length === 0) return null;
          return (
            <div className="indication-preview__field-photos" data-testid="preview-field-photos">
              <span className="indication-preview__field-photos-label">Field photos</span>
              <FieldPhotosStrip photos={shown} />
            </div>
          );
        })()}

        {/* Inline-editable fields — only show fields that have values */}
        <div className="indication-preview__fields">
          {INLINE_FIELDS
            .filter((f) => !!indication.properties[f.key])
            .map((f) => (
            <label key={f.key} className="indication-preview__field-label">
              <span>{f.label}</span>
              <CommitOnBlurField
                className="indication-preview__field-input"
                style={{
                  ...fieldWidth(indication.properties[f.key] || ''),
                  color: FIELD_VALUE_COLORS[f.key],
                  fontWeight: 600,
                }}
                value={indication.properties[f.key] || ''}
                placeholder="—"
                onCommit={(v) => handleFieldChange(f.key, v)}
              />
              {f.key === 'module' && plan?.result.kind === 'grid' && !plan.result.isString && (
                <span className="indication-preview__field-suffix">
                  /{plan.result.cell.totalModules}
                </span>
              )}
            </label>
          ))}
          {/* Custom fields from label schema */}
          {indication.properties.customFields && Object.entries(indication.properties.customFields)
            .filter(([, v]) => !!v)
            .map(([key, value]) => (
            <label key={`custom-${key}`} className="indication-preview__field-label">
              <span>{key}</span>
              <CommitOnBlurField
                className="indication-preview__field-input"
                value={value}
                placeholder="—"
                onCommit={(v) => {
                  const updated = { ...indication.properties.customFields, [key]: v };
                  updateIndication(indicationId, { properties: { customFields: updated } });
                }}
              />
            </label>
          ))}
        </div>

        {/* "Grid says" chip — conflicts amber, blank-fills neutral; writes
            ONLY on Accept; never rendered for share viewers (plan is null). */}
        {chipEntries.length > 0 && plan && (
          <div className="indication-preview__grid-chip">
            <span className="indication-preview__grid-chip-title">⌗ grid says</span>
            {chipEntries.map((e) => (
              <span
                key={e.key}
                className={`indication-preview__grid-chip-item${e.conflict !== undefined ? ' indication-preview__grid-chip-item--conflict' : ''}`}
                title={e.conflict !== undefined ? `stored: ${e.conflict}` : 'stored value is empty'}
              >
                {e.key} {e.value}
              </span>
            ))}
            {/* sampling aids: is this a clean disagreement or a coin flip? */}
            {plan.result.kind === 'grid' && plan.result.ambiguous && (
              <span className="indication-preview__grid-chip-flag" title="Two cells overlap this marker almost equally — the answer is a near-tie">
                ≈ ambiguous
              </span>
            )}
            {plan.result.kind === 'grid' && !plan.result.isString && plan.result.significantCellCount > 2 && (
              <span className="indication-preview__grid-chip-flag" title={`${plan.result.significantCellCount} cells overlap this single-module marker — the grid likely has overlapping cells here`}>
                ⧉ overlapping cells
              </span>
            )}
            <button
              type="button"
              className="indication-preview__grid-chip-accept"
              onClick={() => {
                const updates = plan.conflictUpdates ?? plan.updates;
                if (updates) updateIndication(indicationId, { properties: updates });
              }}
            >
              Accept
            </button>
            {/* "Keep stored" (c6, 2026-09-03): the sampling look and the
                protection are ONE gesture — ground-truthing "stored is
                right, grid is wrong" stamps 'human' on the disputed
                fields, so re-derives and the bulk accept tier never touch
                them, and the chip stops re-litigating (respectHuman). */}
            {plan.conflicts.length > 0 && (
              <button
                type="button"
                className="indication-preview__grid-chip-keep"
                title="The stored value is right — protect it from re-derives and bulk runs"
                onClick={() => {
                  updateIndication(indicationId, {
                    properties: humanEditProvenance(
                      indication.properties as unknown as Record<string, unknown>,
                      plan.conflicts.map((c) => c.field),
                      // the grid state this verdict was decided against —
                      // lets the suppression expire if the grid changes
                      plan.result.kind === 'grid' ? plan.result.source : undefined,
                    ),
                  });
                }}
              >
                Keep stored
              </button>
            )}
          </div>
        )}

        {/* Notes box — ΔT label (styled) + editable textarea */}
        <div className="indication-preview__notes-box">
          {thermalData?.rois?.some((r) => r.stats && r.stats.delta_t > 0) && (
            <span className="indication-preview__delta-tag">
              {thermalData.rois
                .filter((r) => r.stats && r.stats.delta_t > 0)
                .map((r, i) => `ΔT${thermalData.rois.length > 1 ? (i + 1) : ''} ${r.stats!.delta_t.toFixed(1)}°C`)
                .join(' · ')}
            </span>
          )}
          <CommitOnBlurField
            multiline
            rows={1}
            className="indication-preview__notes-input"
            value={userNotes}
            placeholder="Notes..."
            onCommit={handleNotesChange}
          />
        </div>

        {/* Footer: checkbox + action buttons */}
        {/* F-02E-2B: share viewers see no Issue toggle / Edit / Delete —
            all three trigger backend mutations (updateIndication +
            issue sync). Close remains so the visitor can dismiss. */}
        <div className="indication-preview__actions" data-testid="indication-preview-actions">
          {!isReadOnly && (
            <label className="indication-preview__checkbox">
              <input
                type="checkbox"
                checked={!!indication.properties.createIssue}
                onChange={(e) => {
                  const checked = e.target.checked;
                  updateIndication(indicationId, { properties: { createIssue: checked } });
                  if (indication) {
                    const siteId = useProjectStore.getState().meta.siteId ?? '';
                    useTaskStore.getState().syncOrRemoveIssueForIndication(indication, siteId, checked);
                  }
                }}
              />
              <span>Issue</span>
            </label>
          )}
          {!isReadOnly && (
            <button
              className="indication-preview__btn indication-preview__btn--primary"
              onClick={() => onEditFields(indicationId)}
              data-testid="indication-preview-edit"
            >
              Edit
            </button>
          )}
          {!isReadOnly && (
            <button
              className="indication-preview__btn indication-preview__btn--danger"
              onClick={() => onDelete(indicationId)}
              data-testid="indication-preview-delete"
            >
              Delete
            </button>
          )}
          {/* Field navigation — pure client-side, so share viewers get it too. */}
          <button
            className="indication-preview__btn"
            onClick={() => {
              const c = indCenter(indication.geometry as GeoJSON.Geometry);
              if (!c) return;
              const label =
                indication.properties.title ||
                (indication.type === 'Custom' && indication.properties.customTypeName) ||
                typeConfig?.label ||
                indication.type;
              useMapStore.getState().setGuideTarget({ lng: c[0], lat: c[1], label });
              onClose();
            }}
            data-testid="indication-preview-guide"
          >
            Guide
          </button>
          <button className="indication-preview__btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PhotoSection: handles Thermal/Visual/Zoom toggle ──
// Exported so the FlightPathPopup mini-map photo popup can reuse the exact same
// thermal/visual/zoom resolution + toggle (V-01C). `imageSrc` is the resolved
// thermal/primary image; `visualImage`/`zoomImage` are already-resolved URLs
// (Drive thumbnail or resolveImageSrc output) or undefined when absent.

type PhotoView = 'thermal' | 'visual' | 'zoom';

export function PhotoSection({
  imageSrc,
  imageFullSrc,
  thermalData,
  hasThermalOverlay,
  thermalImgRef,
  noop,
  visualImage,
  visualFullSrc,
  zoomImage,
  zoomFullSrc,
}: {
  imageSrc: string;
  /** Full-res companion of `imageSrc` for the progressive zoom upgrade (Drive
   *  photos only — omit when imageSrc is already full-res). */
  imageFullSrc?: string;
  thermalData: any;
  hasThermalOverlay: any;
  thermalImgRef: React.RefObject<HTMLImageElement | null>;
  noop: () => void;
  visualImage?: string;
  visualFullSrc?: string;
  zoomImage?: string;
  zoomFullSrc?: string;
}) {
  const hasToggle = !!visualImage || !!zoomImage;
  const [view, setView] = useState<PhotoView>('thermal');

  // Sticky preference falls back to Thermal when the current indication
  // doesn't have the variant the user previously selected (e.g. switching
  // from a Drive photo in Visual mode to a local photo with no visual).
  // Derived during render so we never paint a blank frame; `view` itself
  // stays untouched so the preference re-applies on the next photo that
  // does have the variant.
  const effectiveView: PhotoView =
    (view === 'visual' && !visualImage) || (view === 'zoom' && !zoomImage)
      ? 'thermal'
      : view;

  // Preload visual/zoom images in background so they're cached when user switches
  useEffect(() => {
    if (visualImage) { const img = new Image(); img.src = visualImage; }
    if (zoomImage) { const img = new Image(); img.src = zoomImage; }
  }, [visualImage, zoomImage]);

  // Build available views
  const views: { id: PhotoView; label: string }[] = [{ id: 'thermal', label: 'Thermal' }];
  if (visualImage) views.push({ id: 'visual', label: 'Visual' });
  if (zoomImage) views.push({ id: 'zoom', label: 'Zoom' });

  return (
    <>
      {/* Toggle buttons — only shown when visual/zoom pairs exist */}
      {hasToggle && (
        <div className="indication-preview__view-toggle">
          {views.map((v) => (
            <button
              key={v.id}
              className={`indication-preview__view-btn ${effectiveView === v.id ? 'indication-preview__view-btn--active' : ''}`}
              onClick={() => setView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* Thermal view — zoomable like Visual/Zoom. With a thermal overlay the
          annotator (img + ROI canvas) scales as one unit via children mode;
          the preview annotator is non-interactive so its pointer math is
          unaffected by the transform. */}
      {effectiveView === 'thermal' && (
        <div className="indication-preview__thumb-wrap">
          {hasThermalOverlay ? (
            <ZoomableImage src={imageSrc}>
              <ThermalAnnotator
                imageSrc={imageSrc}
                rois={thermalData.rois || []}
                points={thermalData.points || []}
                activeRoiIndex={-1}
                onPointerDown={noop}
                onPointerMove={noop}
                onPointerUp={noop}
                imgRef={thermalImgRef}
                interactive={false}
              />
            </ZoomableImage>
          ) : (
            <ZoomableImage
              src={imageSrc}
              fullSrc={imageFullSrc}
              alt=""
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      )}

      {/* Visual view — zoomable Drive image */}
      {effectiveView === 'visual' && visualImage && (
        <div className="indication-preview__thumb-wrap">
          <ZoomableImage src={visualImage} fullSrc={visualFullSrc} alt="Visual" />
        </div>
      )}

      {/* Zoom view — zoomable Drive image */}
      {effectiveView === 'zoom' && zoomImage && (
        <div className="indication-preview__thumb-wrap">
          <ZoomableImage src={zoomImage} fullSrc={zoomFullSrc} alt="Zoom" />
        </div>
      )}
    </>
  );
}

// ── CommitOnBlurField (MBX-04) ──────────────────────────────────────────────
// A text input / textarea that edits in LOCAL state and commits to the store
// only on blur — plus a flush on unmount so closing the popup (Escape / Close)
// while still focused doesn't drop the last edit.
//
// The previous inputs were controlled directly from the store and wrote via
// `updateIndication` on EVERY keystroke. That path is async + optimistic: a
// synchronous local `set()` followed by an entity PATCH. Under rapid typing,
// successive PATCHes carry the same stale `_version`, the later ones 409, and
// the conflict handler reloads authoritative server state — replacing the
// in-flight value and clobbering characters typed after the write (dropped
// keys). Editing locally and committing once on blur removes that race; the
// store sees one write per edit session instead of one per keystroke.
//
// The field reseeds from the store value only while it is NOT focused, so
// external writes (topology auto-fill, conflict reloads) still flow in without
// stomping on what the user is actively typing.
function CommitOnBlurField({
  value,
  onCommit,
  className,
  placeholder,
  multiline = false,
  rows,
  style,
}: {
  value: string;
  onCommit: (next: string) => void;
  className?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  style?: React.CSSProperties;
}) {
  const [local, setLocal] = useState(value);
  const localRef = useRef(value);
  localRef.current = local;
  const committedRef = useRef(value);
  const focusedRef = useRef(false);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // Reseed from external store changes only when the user isn't editing.
  useEffect(() => {
    if (!focusedRef.current && value !== localRef.current) {
      setLocal(value);
    }
    committedRef.current = value;
  }, [value]);

  const commit = useCallback(() => {
    if (localRef.current !== committedRef.current) {
      committedRef.current = localRef.current;
      onCommitRef.current(localRef.current);
    }
  }, []);

  // Flush a pending edit if the popup closes (Escape / Close) while focused.
  useEffect(() => commit, [commit]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setLocal(e.target.value);
  const handleFocus = () => { focusedRef.current = true; };
  const handleBlur = () => { focusedRef.current = false; commit(); };

  if (multiline) {
    return (
      <textarea
        className={className}
        value={local}
        placeholder={placeholder}
        rows={rows}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  }
  return (
    <input
      type="text"
      className={className}
      style={style}
      value={local}
      placeholder={placeholder}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}
